import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { z } from 'zod';

import { usePages } from '../../../../api/portal/usePagesApi';
import DatePicker from '../../../../components/DatePicker/DatePicker';
import Input from '../../../../components/Input/Input';
import InputSelectDropdown, {
  InputSelectDropdownOption
} from '../../../../components/InputSelectDropdown/InputSelectDropdown';
import { cn } from '../../../../components/lib/utils';
import RichText from '../../../../components/RichText/RichText';
import Switch from '../../../../components/Switch/Switch';
import Textarea from '../../../../components/Textarea/Textarea';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '../../../../components/ui';
import UploadImage from '../../../../components/UploadImage/UploadImage';
import { sanitizeSlug } from '../../../../utils/portalUtils';
import { PagesFormProps } from '../../types';
import {
  EPagesRadioButtonOptions,
  EPagesTemplateOptions,
  PAGES_LEVEL_OPTIONS,
  pageTemplateOptions,
  visibilityOptions
} from '../constants';
import { pageFormSchema } from '../validation-schema';

export const SettingsForm: FC<PagesFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { pageId } = useParams();
  const { getPages } = usePages(pageId);

  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors }
  } = useFormContext<z.infer<typeof pageFormSchema>>();

  const isPageTemplateDisabled =
    edit &&
    (watch('pageTemplate') === EPagesTemplateOptions.HOME ||
      watch('pageTemplate') === EPagesTemplateOptions.STORE);

  const mainPages: InputSelectDropdownOption[] = useMemo(() => {
    if (!getPages.data?.pages) return [];

    return getPages.data.pages
      .filter((page: any) => page.isSubPage === false && page.id !== pageId)
      .map((page: any) => ({
        value: page.id,
        id: page.id,
        label: page.pageName
      }));
  }, [getPages.data, pageId]);

  const pageNameValue = watch('pageName');

  useEffect(() => {
    if (pageNameValue && !isPageTemplateDisabled) {
      const formattedSlug = sanitizeSlug(pageNameValue);
      setValue('slug', formattedSlug);
    }
  }, [pageNameValue, isPageTemplateDisabled]);

  const handleIsSubPageChange = useCallback(
    (selectedValue: string, onChange: (value: boolean) => void) => {
      const isSubPageValue = selectedValue === 'true';
      onChange(isSubPageValue);

      const currentIsSubPage = getValues('isSubPage');
      const currentPriority = getValues('priority');
      const currentSubPagePriority = getValues('subPagePriority');

      if (!isSubPageValue) {
        setValue('parentPageId', undefined, {
          shouldDirty: true,
          shouldValidate: true
        });
        setValue('priority', currentSubPagePriority ?? 0, {
          shouldDirty: true,
          shouldValidate: true
        });
        setValue('subPagePriority', 0, {
          shouldDirty: true,
          shouldValidate: true
        });
      } else if (currentIsSubPage === false) {
        if (!getValues('parentPageId') && mainPages.length > 0) {
          setValue('parentPageId', mainPages[0].value.toString(), {
            shouldDirty: true,
            shouldValidate: true
          });
        }
        setValue('subPagePriority', currentPriority ?? 0, {
          shouldDirty: true,
          shouldValidate: true
        });
        setValue('priority', 0, {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    },
    [mainPages]
  );

  return (
    <>
      <FormField
        control={control}
        name="pageName"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input
                isRequired
                label="Page Name"
                placeholder="e.g., Blog"
                state={errors.pageName?.message ? 'error' : 'default'}
                description={errors.pageName?.message}
                {...field}
                className="mb-5"
                data-testid="PagesForm-PageName"
              />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="pageIcon"
        render={({ field: { onChange, value } }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Add an icon"
                description={`Add an icon to your page.\nImage will be scaled to 130px x 60px`}
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className={cn(
                  'w-full mb-5',
                  errors.pageIcon?.message ? 'mb-0' : ''
                )}
                isRequired
                selectedImageUrl={value}
                data-testid="PagesForm-Upload-PageIcon"
              />
            </FormControl>
            <FormMessage className="mt-1 !mb-5 text-p-s text-base-destructive" />
          </FormItem>
        )}
      />
      <div
        className="grid grid-cols-2 gap-5 mb-9 border-b pb-[50px] border-base-sidebarBorder w-full"
        data-testid="PagesForm-Grid"
      >
        <FormField
          control={control}
          name="slug"
          render={({ field }) => {
            const { value, onChange, onBlur, ...rest } = field;
            return (
              <FormItem>
                <FormControl>
                  <Input
                    label="Portal/"
                    placeholder="e.g., blog"
                    value={value}
                    onChange={onChange}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      setValue('slug', sanitizeSlug(e.target.value), {
                        shouldDirty: true,
                        shouldValidate: true
                      });
                    }}
                    description={errors.slug?.message}
                    state={errors.slug?.message ? 'error' : 'default'}
                    {...rest}
                    disabled={isPageTemplateDisabled}
                    data-testid="PagesForm-Input-Slug"
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />
        <FormField
          control={control}
          name="pageTemplate"
          render={({ field: { onChange } }) => (
            <FormItem>
              <FormControl>
                <InputSelectDropdown
                  label="Page Type"
                  defaultSelected={pageTemplateOptions.find(
                    (option) => option.value === watch('pageTemplate')
                  )}
                  options={pageTemplateOptions}
                  onOptionChange={onChange}
                  disabled={isPageTemplateDisabled}
                  buttonDataTestId="PagesForm-Select-PageType"
                  listDataTestId="PagesForm-Select-PageType-List"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="isSubPage"
          render={({ field: { onChange, value } }) => (
            <FormItem>
              <FormControl>
                <InputSelectDropdown
                  label="Page Level"
                  defaultSelected={PAGES_LEVEL_OPTIONS.find(
                    (option) => option.value === String(value)
                  )}
                  options={PAGES_LEVEL_OPTIONS}
                  onOptionChange={(selectedValue) => {
                    handleIsSubPageChange(selectedValue, onChange);
                  }}
                  disabled={isPageTemplateDisabled}
                  buttonDataTestId="PagesForm-Select-PageLevel"
                  listDataTestId="PagesForm-Select-PageLevel-List"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="parentPageId"
          render={({ field: { onChange, value } }) => (
            <FormItem>
              <FormControl>
                <InputSelectDropdown
                  isRequired={watch('isSubPage')}
                  key={`parent-page-${watch('isSubPage')}-${value || 'none'}`}
                  disabled={!watch('isSubPage') || isPageTemplateDisabled}
                  label="Parent Page"
                  defaultSelected={
                    watch('isSubPage') && value
                      ? mainPages.find(
                          (option: InputSelectDropdownOption) =>
                            option.value === value
                        )
                      : undefined
                  }
                  options={mainPages}
                  onOptionChange={onChange}
                  placeholder="Select parent page"
                  buttonDataTestId="PagesForm-Select-ParentPage"
                  listDataTestId="PagesForm-Select-ParentPage-List"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {!watch('isSubPage') ? (
          <FormField
            control={control}
            name="priority"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="number"
                    label="Priority"
                    {...field}
                    value={field.value ?? ''}
                    data-testid="PagesForm-Input-Priority"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <FormField
            control={control}
            name="subPagePriority"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="number"
                    label="Priority"
                    {...field}
                    value={field.value ?? ''}
                    data-testid="PagesForm-Input-SubPagePriority"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={control}
          name="visibility"
          render={({ field: { onChange } }) => (
            <FormItem>
              <FormControl>
                <InputSelectDropdown
                  label="Visibility"
                  defaultSelected={visibilityOptions.find(
                    (option) => option.value === watch('visibility')
                  )}
                  options={visibilityOptions}
                  onOptionChange={onChange}
                  buttonDataTestId="PagesForm-Select-Visibility"
                  listDataTestId="PagesForm-Select-Visibility-List"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <RichText
        title="Metadata"
        description="Enhance your page's visibility and searchability"
        className="mb-9"
        data-testid="PagesForm-MetadataRichText"
      />
      <FormField
        control={control}
        name="metadataImage"
        render={({ field: { onChange, value } }) => (
          <FormItem>
            <FormControl>
              <UploadImage
                title="Add an image"
                description="The image will be scaled to 32px x 32px"
                buttonText="Upload"
                onUpload={(url: string) => {
                  onChange(url);
                }}
                className="w-full mb-5"
                selectedImageUrl={value}
                data-testid="PagesForm-Upload-MetadataImage"
                buttonDataTestId="PagesForm-Upload-MetadataImage-Btn"
              />
            </FormControl>
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="metadataDescription"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Textarea
                label="Metadata Description"
                placeholder="Write Something..."
                {...field}
                data-testid="PagesForm-Textarea-MetadataDescription"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {watch('state') === EPagesRadioButtonOptions.HIDDEN && (
        <div className="w-full mt-9" data-testid="PagesForm-ScheduleSection">
          <RichText
            title="Schedule publication date"
            description="Set a date for your content to go live"
            className="mb-5"
          />
          <FormField
            control={control}
            name="isPublishDateEnabled"
            render={({ field }) => (
              <Switch
                key={field.value ? 'on' : 'off'}
                className="mb-9"
                label="Enable scheduled publishing"
                description="Turn on scheduled publishing for this content"
                textAlignment="right"
                defaultChecked={field.value}
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                  if (!checked) {
                    setValue('publishDate', '', {
                      shouldDirty: true,
                      shouldValidate: true
                    });
                  }
                }}
                data-testid="PagesForm-Switch-Schedule"
              />
            )}
          />
          <FormField
            name="publishDate"
            control={control}
            render={({ field }) => {
              return (
                <FormItem>
                  <DatePicker
                    shouldExcludePast
                    displayInUTC
                    label="Publish date"
                    infoTooltipContent="All times shown in UTC"
                    disabled={!watch('isPublishDateEnabled')}
                    {...field}
                    dataTestId="PagesForm-DatePicker-PublishDate"
                  />
                </FormItem>
              );
            }}
          />
        </div>
      )}
    </>
  );
};
